/**
 * Template Storage Service
 * Manages CRUD operations for dynamic templates using localStorage
 */

import {
  GeneratedTemplate,
  TemplateMetadata,
  TemplateExport,
  TemplateStorageOptions
} from '../../../types/src/lib/template-types';

const DEFAULT_NAMESPACE = 'cty_templates';
const VERSION_KEY = 'version';
const CURRENT_VERSION = '1.0.0';

class TemplateStorageService {
  private namespace: string;
  private storageType: 'localStorage' | 'indexedDB';

  constructor(options: TemplateStorageOptions = {}) {
    this.namespace = options.namespace || DEFAULT_NAMESPACE;
    this.storageType = options.storage || 'localStorage';
    this.initializeStorage();
  }

  private initializeStorage(): void {
    try {
      const version = localStorage.getItem(`${this.namespace}_${VERSION_KEY}`);
      if (!version) {
        localStorage.setItem(`${this.namespace}_${VERSION_KEY}`, CURRENT_VERSION);
        localStorage.setItem(this.namespace, JSON.stringify([]));
      }
    } catch (error) {
      console.error('Failed to initialize template storage:', error);
    }
  }

  private getStorageKey(): string {
    return this.namespace;
  }

  /**
   * Get all templates from storage
   */
  async getAll(): Promise<GeneratedTemplate[]> {
    try {
      const data = localStorage.getItem(this.getStorageKey());
      if (!data) return [];
      return JSON.parse(data) as GeneratedTemplate[];
    } catch (error) {
      console.error('Failed to retrieve templates:', error);
      return [];
    }
  }

  /**
   * Get a single template by ID
   */
  async getById(id: string): Promise<GeneratedTemplate | null> {
    try {
      const templates = await this.getAll();
      return templates.find(t => t.metadata.id === id) || null;
    } catch (error) {
      console.error('Failed to retrieve template:', error);
      return null;
    }
  }

  /**
   * Get templates by agent type
   */
  async getByAgentType(agentType: string): Promise<GeneratedTemplate[]> {
    try {
      const templates = await this.getAll();
      return templates.filter(t => t.metadata.agentType === agentType);
    } catch (error) {
      console.error('Failed to retrieve templates by agent type:', error);
      return [];
    }
  }

  /**
   * Get templates by category
   */
  async getByCategory(category: string): Promise<GeneratedTemplate[]> {
    try {
      const templates = await this.getAll();
      return templates.filter(t => t.metadata.category === category);
    } catch (error) {
      console.error('Failed to retrieve templates by category:', error);
      return [];
    }
  }

  /**
   * Search templates by name or description
   */
  async search(query: string): Promise<GeneratedTemplate[]> {
    try {
      const templates = await this.getAll();
      const lowerQuery = query.toLowerCase();
      return templates.filter(t =>
        t.metadata.name.toLowerCase().includes(lowerQuery) ||
        t.metadata.description.toLowerCase().includes(lowerQuery) ||
        t.metadata.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
      );
    } catch (error) {
      console.error('Failed to search templates:', error);
      return [];
    }
  }

  /**
   * Save a new template or update existing one
   */
  async save(template: GeneratedTemplate): Promise<boolean> {
    try {
      const templates = await this.getAll();
      const existingIndex = templates.findIndex(t => t.metadata.id === template.metadata.id);

      if (existingIndex >= 0) {
        // Update existing template
        template.metadata.updatedAt = new Date().toISOString();
        template.metadata.version = (template.metadata.version || 0) + 1;
        templates[existingIndex] = template;
      } else {
        // Add new template
        template.metadata.createdAt = template.metadata.createdAt || new Date().toISOString();
        template.metadata.updatedAt = new Date().toISOString();
        template.metadata.version = 1;
        templates.push(template);
      }

      localStorage.setItem(this.getStorageKey(), JSON.stringify(templates));
      return true;
    } catch (error) {
      console.error('Failed to save template:', error);
      return false;
    }
  }

  /**
   * Delete a template by ID
   */
  async delete(id: string): Promise<boolean> {
    try {
      const templates = await this.getAll();
      const filtered = templates.filter(t => t.metadata.id !== id);

      if (filtered.length === templates.length) {
        console.warn(`Template with id ${id} not found`);
        return false;
      }

      localStorage.setItem(this.getStorageKey(), JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Failed to delete template:', error);
      return false;
    }
  }

  /**
   * Delete multiple templates by IDs
   */
  async deleteMany(ids: string[]): Promise<number> {
    try {
      const templates = await this.getAll();
      const filtered = templates.filter(t => !ids.includes(t.metadata.id));
      const deletedCount = templates.length - filtered.length;

      localStorage.setItem(this.getStorageKey(), JSON.stringify(filtered));
      return deletedCount;
    } catch (error) {
      console.error('Failed to delete templates:', error);
      return 0;
    }
  }

  /**
   * Update template metadata only
   */
  async updateMetadata(id: string, metadata: Partial<TemplateMetadata>): Promise<boolean> {
    try {
      const template = await this.getById(id);
      if (!template) {
        console.warn(`Template with id ${id} not found`);
        return false;
      }

      template.metadata = {
        ...template.metadata,
        ...metadata,
        id: template.metadata.id, // Prevent ID change
        updatedAt: new Date().toISOString()
      };

      return await this.save(template);
    } catch (error) {
      console.error('Failed to update template metadata:', error);
      return false;
    }
  }

  /**
   * Duplicate a template with a new ID
   */
  async duplicate(id: string, newName?: string): Promise<string | null> {
    try {
      const template = await this.getById(id);
      if (!template) {
        console.warn(`Template with id ${id} not found`);
        return null;
      }

      const newId = `${template.metadata.id}_copy_${Date.now()}`;
      const duplicatedTemplate: GeneratedTemplate = {
        ...template,
        metadata: {
          ...template.metadata,
          id: newId,
          name: newName || `${template.metadata.name} (Copy)`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1
        }
      };

      const success = await this.save(duplicatedTemplate);
      return success ? newId : null;
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      return null;
    }
  }

  /**
   * Export templates to JSON
   */
  async export(ids?: string[]): Promise<TemplateExport> {
    try {
      let templates = await this.getAll();

      if (ids && ids.length > 0) {
        templates = templates.filter(t => ids.includes(t.metadata.id));
      }

      return {
        version: CURRENT_VERSION,
        exportDate: new Date().toISOString(),
        templates
      };
    } catch (error) {
      console.error('Failed to export templates:', error);
      return {
        version: CURRENT_VERSION,
        exportDate: new Date().toISOString(),
        templates: []
      };
    }
  }

  /**
   * Import templates from JSON
   */
  async import(exportData: TemplateExport, overwrite: boolean = false): Promise<{
    success: number;
    failed: number;
    skipped: number;
  }> {
    let success = 0;
    let failed = 0;
    let skipped = 0;

    try {
      const existingTemplates = await this.getAll();
      const existingIds = new Set(existingTemplates.map(t => t.metadata.id));

      for (const template of exportData.templates) {
        try {
          if (existingIds.has(template.metadata.id) && !overwrite) {
            skipped++;
            continue;
          }

          const saveSuccess = await this.save(template);
          if (saveSuccess) {
            success++;
          } else {
            failed++;
          }
        } catch (error) {
          console.error(`Failed to import template ${template.metadata.id}:`, error);
          failed++;
        }
      }

      return { success, failed, skipped };
    } catch (error) {
      console.error('Failed to import templates:', error);
      return { success, failed, skipped };
    }
  }

  /**
   * Clear all templates
   */
  async clear(): Promise<boolean> {
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify([]));
      return true;
    } catch (error) {
      console.error('Failed to clear templates:', error);
      return false;
    }
  }

  /**
   * Get storage statistics
   */
  async getStats(): Promise<{
    total: number;
    byAgentType: Record<string, number>;
    byCategory: Record<string, number>;
    storageSize: number;
  }> {
    try {
      const templates = await this.getAll();
      const byAgentType: Record<string, number> = {};
      const byCategory: Record<string, number> = {};

      templates.forEach(template => {
        if (template.metadata.agentType) {
          byAgentType[template.metadata.agentType] =
            (byAgentType[template.metadata.agentType] || 0) + 1;
        }
        if (template.metadata.category) {
          byCategory[template.metadata.category] =
            (byCategory[template.metadata.category] || 0) + 1;
        }
      });

      const storageData = localStorage.getItem(this.getStorageKey()) || '[]';
      const storageSize = new Blob([storageData]).size;

      return {
        total: templates.length,
        byAgentType,
        byCategory,
        storageSize
      };
    } catch (error) {
      console.error('Failed to get storage stats:', error);
      return {
        total: 0,
        byAgentType: {},
        byCategory: {},
        storageSize: 0
      };
    }
  }
}

// Export singleton instance
export const templateStorageService = new TemplateStorageService();

// Also export class for custom instances
export { TemplateStorageService };

